import { useState } from 'react';

import { Badge, Select, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { useDoiChieu } from '../../hooks/useLichSu';
import { tenTepXuat } from '../../nghiep-vu/du-lieu';
import type { KhoaLuotDuLieu } from '../../types';
import { LoiRpa } from '../chung';
import { DongLech } from './BangDuLieu';

/**
 * Đối chiếu dữ liệu cào với dữ liệu xử lý: chọn cột ghép (bỏ trống là máy chủ tự chọn cột chung ổn định), rồi xem dòng
 * THIẾU (đã cào mà không có ở xử lý) và DƯ (có ở xử lý mà không có ở cào). Ghép tính cả dòng trùng.
 */
export function TabDoiChieu({ khoa, luong, maLuot }: { khoa: KhoaLuotDuLieu; luong: string; maLuot: string }) {
  const { t, locale } = useT();
  const [cotGhep, setCotGhep] = useState<string[]>([]);
  const q = useDoiChieu(khoa, cotGhep);

  if (q.isPending) return <Skeleton className="h-48 w-full" />;
  if (q.isError) return <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />;
  const d = q.data;
  const tieuDeThieu = t('rpaDuLieu.thieuTieuDe', { so: soVN(d.missing.total, locale) });
  const tieuDeDu = t('rpaDuLieu.duTieuDe', { so: soVN(d.extra.total, locale) });

  return (
    <div className="min-w-0 space-y-5">
      <div className="space-y-1">
        <label htmlFor="rpa-cot-ghep" className="block text-sm text-content-primary">
          {t('rpaDuLieu.cotGhepNhan')}
        </label>
        <Select<string[]>
          id="rpa-cot-ghep"
          mode="multiple"
          allowClear
          className="w-full"
          placeholder={t('rpaDuLieu.cotGhepMacDinh')}
          value={cotGhep}
          onChange={setCotGhep}
          loading={q.isFetching}
          options={d.availableKeys.map((k) => ({ value: k, label: k }))}
        />
        <p className="text-xs text-content-muted">
          {d.keys.length ? t('rpaDuLieu.dangGhepTheo', { cot: d.keys.join(', ') }) : t('rpaDuLieu.khongCoCotChung')}
        </p>
        {d.droppedKeys.length > 0 && (
          <p className="text-xs text-content-muted">{t('rpaDuLieu.cotBiBo', { cot: d.droppedKeys.join(', ') })}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge variant="success">{t('rpaDuLieu.soKhop', { so: soVN(d.matched, locale) })}</Badge>
        <Badge variant={d.missing.total ? 'danger' : 'neutral'}>{t('rpaDuLieu.soThieu', { so: soVN(d.missing.total, locale) })}</Badge>
        <Badge variant={d.extra.total ? 'warning' : 'neutral'}>{t('rpaDuLieu.soDu', { so: soVN(d.extra.total, locale) })}</Badge>
      </div>
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-danger-700">{tieuDeThieu}</h3>
        <DongLech
          dong={d.missing.rows}
          cot={d.missing.columns}
          tong={d.missing.total}
          cotGhep={d.keys}
          tenTep={tenTepXuat(luong, maLuot, 'thieu', 'csv')}
          rong={t('rpaDuLieu.khongThieu')}
          caption={tieuDeThieu}
        />
      </section>
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-warning-700">{tieuDeDu}</h3>
        <DongLech
          dong={d.extra.rows}
          cot={d.extra.columns}
          tong={d.extra.total}
          cotGhep={d.keys}
          tenTep={tenTepXuat(luong, maLuot, 'du', 'csv')}
          rong={t('rpaDuLieu.khongDu')}
          caption={tieuDeDu}
        />
      </section>
    </div>
  );
}
